const orgsWatched = (state) => {
  return state.currentUser.orgs.filter(org => org.role === 'Watcher')
}

const orgsManaged = (state) => {
  return state.currentUser.orgs.filter(org => org.role === 'Manager')
}

const orgsAdmined = (state) => {
  return state.currentUser.orgs.filter(org => org.role === 'Administrator')
}

const orgsOwned = (state) => {
  return state.currentUser.orgs.filter(org => org.role === 'Account Owner')
}

/**
 * @description checks if user belongs to only one org or if they have selected an organization from the top menu
 * @returns the organization object if the user belongs to a single org, or the selected org in a multi-org setting, or {id: undefined}
 */
const currentSelectedOrg = (state, getters, rootState, rootGetters) => {
  if (state.currentUser.orgs.length === 1 && !getters.isInternal) return state.currentUser.orgs[0]
  if (rootGetters['Organizations/selectedOrg'] !== null && rootGetters['Organizations/selectedOrg'] !== undefined) return rootGetters['Organizations/selectedOrg']
  return { id: undefined }
}

/**
 * @returns if the logged-in user has a system role of 'internal'
 */
const isInternal = (state) => {
  return state.currentUser.role === 'internal'
}
/**
 * @description checks if the logged-in user is multi-org
 * @returns boolean
 */
const isMultiOrg = (state) => {
  if (state.currentUser.orgs && state.currentUser.orgs.length > 1) return true
  return false
}

/**
 * @description checks if the logged-in user is an owner of the currently-selected org
 * @returns if the logged-in user can function as an Account Owner for the current org
 */
 const isOrgOwner = (state, getters) => {
  if (getters.isInternal) return true
  const orgId = getters.currentSelectedOrg.id
  if (getters.orgsOwned.some(org => org.id === orgId)) return true
  return false
}

/**
 * @description checks if the logged-in user is internal or belongs to the passed org ID as a administrator role
 * @returns if the logged-in user can function as a 'admin' for the passed org ID
 */
const canAdminOrg = (state, getters) => (orgId) => {
  if (getters.isInternal) return true
  if (typeof orgId === 'string') orgId = Number.parseInt(orgId)
  if (getters.orgsAdmined.some(org => org.id === orgId) || getters.orgsOwned.some(org => org.id === orgId)) return true
  return false
}

/**
 * @description checks if an org is selected and the logged-in user is internal or belongs to the currently selected org as a administrator role
 * @returns if the logged-in user can function as a 'admin' for the currently selected org
 */
const canAdminSelectedOrg = (state, getters) => {
  if (!getters.currentSelectedOrg.id) return false
  if (getters.isInternal) return true
  if (getters.currentSelectedOrg.id && (getters.orgsOwned.some(org => org.id === getters.currentSelectedOrg.id) || getters.orgsAdmined.some(org => org.id === getters.currentSelectedOrg.id))) return true
  return false
}

/**
 * @description checks if the logged-in user is internal or belongs to the passed org ID as a manager or admin
 * @returns if the logged-in user can function as a 'manager' to the passed org ID
 */
const canManageOrg = (state, getters) => (orgId) => {
  if (getters.isInternal || getters.canAdminOrg(orgId)) return true
  if (typeof orgId === 'string') orgId = Number.parseInt(orgId)
  if (getters.orgsManaged.some(org => org.id === orgId)) return true
  return false
}

/**
 * @description checks if an org is selected and the logged-in user is internal or belongs to the currently selected org as a manager or admin
 * @returns if the logged-in user can function as a 'manager' for the currently selected org
 */
const canManageSelectedOrg = (state, getters) => {
  if (!getters.currentSelectedOrg.id) return false
  if (getters.isInternal || getters.canAdminSelectedOrg) return true
  if (getters.currentSelectedOrg.id && getters.orgsManaged.some(org => org.id === getters.currentSelectedOrg.id)) return true
  return false
}

/**
 * @description checks if the logged-in user is internal or belongs to the currently selected org as a admin, manager, or watcher role
 * @returns if the logged-in user can function as a 'watcher' for the currently selected org
 */
const canWatchOrg = (state, getters) => {
  if (getters.isInternal || getters.canAdminOrg || getters.canManageOrg) return true
  if (getters.currentSelectedOrg.id && getters.orgsWatched.some(org => org.id === getters.currentSelectedOrg.id)) return true
  return false
}

/**
 * @description checks if the logged-in user is internal or includes the passed property ID in the set of editable properties
 * @returns if the logged-in user can edit the specified property's details
 */
const canEditProperty = (state, getters) => (propertyId) => {
  if (getters.isInternal) return true
  if (typeof propertyId === 'string') propertyId = Number.parseInt(propertyId)
  if (state.userEditableProperties.some(id => id === propertyId)) return true
  return false
}

/**
 * @descriptions checks if the logged-in user is internal or includes the currently selected property in the set of editable properties
 * @returns if the logged-in user can edit the currently selected property's details
 */
const canEditSelectedProperty = (state, getters) => {
  if (getters.isInternal) return true
  if (state.currentSelectedProperty.id && state.userEditableProperties.some(id => id === state.currentSelectedProperty.id)) return true
  return false
}

/**
 * @description checks if the logged-in user is internal or includes the passed property ID in the set of viewable properties
 * @returns if the logged-in user can view the specified property's details
 */
const canViewProperty = (state, getters) => (propertyId) => {
  if (getters.isInternal) return true
  if (typeof propertyId === 'string') propertyId = Number.parseInt(propertyId)
  if (state.userViewableProperties.some(id => id === propertyId)) return true
  return false
}

/**
 * @description checks if the logged-in user is internal or includes the currently selected property in the set of viewable properties
 * @returns if the logged-in user can view the currently selected property's details
 */
const canViewSelectedProperty = (state, getters) => {
  if (getters.isInternal) return true
  if (state.currentSelectedProperty.id && state.userViewableProperties.some(property => property.id === state.currentSelectedProperty.id)) return true
  return false
}

/**
 * @description checks if the logged-in user is internal or is an administrator of an org that the currently selected user belongs to.
 * @returns if the logged-in user can edit the settings of the currently selected user
 */
const canAdminSelectedUser = (state, getters, rootState, rootGetters) => {
  if (getters.isInternal) return true
  if (getters.canAdminSelectedOrg && rootGetters['Users/userData'].orgs && rootGetters['Users/userData'].orgs.some(org => org.id === getters.currentSelectedOrg.id)) return true
  return false
}

/**
 *
 * @description checks if the logged-in user is internal or is an administrator of the passed org and if the passed user belongs to that org
 * @returns if the logged-in user can edit the settings of the passed in user
 */
const canAdminUser = (state, getters) => (user, orgId) => {
  if (getters.isInternal) return true
  if (getters.canAdminOrg(orgId) && user.orgs && user.orgs.some(org => org.id === orgId)) return true
  return false
}

/**
 *
 * @description checks if the logged-in user is internal or is an admin of the passed org and if the user (referenced by ID) belongs to that org
 * @returns if the logged-in user can edit the settings of the passed in user
 */

const canAdminUserOnOrg = (state, getters) => (user, orgId) => {
  if (getters.isInternal) return true
  if (typeof orgId === 'string') orgId = parseInt(orgId)
  if (getters.canAdminOrg(orgId) && user && user.orgs && user.orgs.some(org => org.id === orgId)) return true
  return false
}

/**
 * @returns if the currently selected user is the same as the logged-in user
 */
const isUserSelf = (state, getters, rootState, rootGetters) => {
  return state.currentUser.id === rootGetters['Users/userData'].id
}

/**
 *
 * @description checks the current users role on the current selected org
 * @returns if the the user is attached to an org
 *
 */
const userRoleOnOrg = (state, getters, rootState, rootGetters) => {
  const user = rootGetters['Users/userData']
  const org = getters.currentSelectedOrg
  if (user.orgs && org) {
    const belongsToOrg = user.orgs.find(userOrg => userOrg.id === org.id)
    if (belongsToOrg) return belongsToOrg.role
  }
  return null
}

/**
 *
 * @description checks if the current user has access to reservations
 * @returns if the the user has access to NightAgent
 *
 */
const hasNightAgent = (state, getters, rootState, rootGetters) => {
  return (rootGetters['Organizations/orgData'].responseCenter)
}

/**
 *
 * @description checks if the current user is the NightAgent account
 * @returns whether the user is the NightAgent account
 */
const isNightAgent = (state, getters, rootState, rootGetters) => {
  const NIGHT_AGENT_PROD_USER_IDS = [
    1335, // noiseaware@stealthmonitoring.com
    12877, // autoresolve@noiseaware.com
  ]
  return NIGHT_AGENT_PROD_USER_IDS.includes(rootGetters['Users/currentUserId'])
}

export default {
  orgsWatched,
  orgsManaged,
  orgsAdmined,
  orgsOwned,
  currentSelectedOrg,
  canWatchOrg,
  canManageOrg,
  canManageSelectedOrg,
  canAdminOrg,
  canAdminSelectedOrg,
  canEditProperty,
  canEditSelectedProperty,
  canViewProperty,
  canViewSelectedProperty,
  isInternal,
  canAdminUser,
  canAdminUserOnOrg,
  canAdminSelectedUser,
  isUserSelf,
  isMultiOrg,
  userRoleOnOrg,
  hasNightAgent,
  isNightAgent,
  isOrgOwner,
}
